import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        // Check if we have a valid session from the reset link
        if (supabase) {
            supabase.auth.getSession().then(({ data: { session } }) => {
                if (!session) {
                    // No session, redirect to home
                    navigate('/');
                }
            });
        }
    }, [navigate]);

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (password.length < 6) {
                throw new Error('Password must be at least 6 characters');
            }

            if (password !== confirmPassword) {
                throw new Error('Passwords do not match');
            }

            if (supabase) {
                const { error } = await supabase.auth.updateUser({ password });
                if (error) throw error;
                setSuccess(true);
                setTimeout(() => navigate('/'), 3000);
            } else {
                // Mock mode
                await new Promise(resolve => setTimeout(resolve, 1000));
                setSuccess(true);
                setTimeout(() => navigate('/'), 3000);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-transparent flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-transparent dark:border-slate-700">
                <div className="p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-orange-100 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400 rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
                            <i className="fas fa-key"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Set New Password</h2>
                        <p className="text-slate-500 dark:text-slate-400 mt-2">Enter your new password below</p>
                    </div>

                    {success ? (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-950/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center text-2xl mx-auto mb-4">
                                <i className="fas fa-check"></i>
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Password Reset Successfully!</h3>
                            <p className="text-slate-500 dark:text-slate-400">Redirecting you to the home page...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-350 mb-1">New Password</label>
                                <div className="relative">
                                    <i className="fas fa-lock absolute left-3 top-3 text-slate-400"></i>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400"
                                        placeholder="••••••••"
                                        required
                                        minLength={6}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-350 mb-1">Confirm Password</label>
                                <div className="relative">
                                    <i className="fas fa-lock absolute left-3 top-3 text-slate-400"></i>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder-slate-400"
                                        placeholder="••••••••"
                                        required
                                        minLength={6}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 text-sm rounded-lg flex items-center border border-red-100 dark:border-red-900/30">
                                    <i className="fas fa-exclamation-circle mr-2"></i> {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-slate-900 dark:bg-orange-600 text-white py-3 rounded-lg font-bold hover:bg-slate-800 dark:hover:bg-orange-700 transition-colors shadow-lg disabled:opacity-70 flex justify-center items-center"
                            >
                                {loading ? <i className="fas fa-circle-notch fa-spin"></i> : 'Reset Password'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
